import React, { useState, useRef, useCallback, useEffect } from "react";
import { Container, DropArea } from "./styles";
import { IoCloudUploadOutline } from "react-icons/io5";
import { GoCheck, GoArrowRight } from "react-icons/go";
import { HiOutlineDocumentText, HiOutlineShieldCheck, HiOutlineCloudArrowUp, HiOutlineCpuChip } from "react-icons/hi2";
import VidalLogo from "../../imgs/logo.png";
import { useAuth } from "../../contexts/AuthContext";
import authService from "../../services/authService";
import { toast } from "react-toastify";
import { parseSpedFile } from "../../utils/spedParser";
import ReviewScreen from "./components/ReviewScreen";
import ProcessingScreen from "./components/ProcessingScreen";
import UploadProgressModal from "./components/UploadProgressModal";

const API_URL = process.env.REACT_APP_API_URL || "https://api.vidal-app.com";

// Gerar ID único para operações de upload
function generateOperationId() {
  return 'op_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
}

// Helper: Envia um único arquivo via XHR com progresso
function uploadSingleFile(url, fieldName, file, token, extraFields = {}) {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append(fieldName, file);
    Object.entries(extraFields).forEach(([k, v]) => formData.append(k, v));

    const xhr = new XMLHttpRequest();
    let lastPercent = 0;

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        lastPercent = Math.round((e.loaded / e.total) * 100);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { resolve({ success: true }); }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.error || err.message || `Erro ${xhr.status}`));
        } catch {
          reject(new Error(`Erro ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Erro de conexão')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelado')));

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);

    // Expose progress getter
    xhr._getProgress = () => lastPercent;
    // Store xhr ref for potential abort
    uploadSingleFile._currentXhr = xhr;
  });
}

export default function Upload({ onVoltar, hideBackButton = false }) {
  const { currentProject } = useAuth();
  
  // Estados de Fluxo
  const [stage, setStage] = useState('drop'); 
  const [operationId, setOperationId] = useState(null); // Added operationId state
  
  // Dados
  const [filesStats, setFilesStats] = useState([]);
  const [rawFiles, setRawFiles] = useState([]);
  
  // UI States
  const [isDragging, setIsDragging] = useState(false);
  const [warning, setWarning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [uploadingZip, setUploadingZip] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true); // Inicializa como true para mostrar loader
  
  // Estado inicial do processamento (recuperado do backend após reload)
  const [initialProcessingState, setInitialProcessingState] = useState(null);
  
  // Smart Upload (modal de progresso para arquivos genéricos/compactados)
  const [smartUploadActive, setSmartUploadActive] = useState(false);
  const [smartUploadOperationId, setSmartUploadOperationId] = useState(null);
  
  const fileInputRef = useRef(null);

  // Restore session from localStorage using useEffect
  React.useEffect(() => {
     const projectId = currentProject?._id || authService.getSelectedProject();
     if (!projectId) {
         setCheckingStatus(false);
         return;
     }
     
     const key = `ravena_active_upload_${projectId}`;
     const savedState = localStorage.getItem(key);
     let restored = false;
     
     if (savedState) {
         try {
             // Lógica de restauração...
             const { stage: savedStage, operationId: savedOpId, spedType, timestamp } = JSON.parse(savedState);
             const isRecent = (Date.now() - timestamp) < 24 * 60 * 60 * 1000;
             if (isRecent && savedStage === 'processing') {
                 console.log(`[Upload] Restoring active session for project ${projectId}: spedType=${spedType}, operationId=${savedOpId}`);
                 setOperationId(savedOpId);
                 setStage('processing');
                 if (spedType === 'mixed') {
                     setFilesStats([{ tipoSped: 'ICMS_IPI' }, { tipoSped: 'PIS_COFINS' }]);
                 } else if (spedType === 'pis') {
                     setFilesStats([{ tipoSped: 'PIS_COFINS' }]); 
                 } else if (spedType === 'icms') {
                     setFilesStats([{ tipoSped: 'ICMS_IPI' }]);
                 }
                 restored = true;

                 // VERIFICAÇÃO DE LIVENESS: Confirmar com backend
                 const checkAlive = async () => {
                    try {
                        const token = authService.getToken();
                        const response = await fetch(`${API_URL}/speds/processing-status/${projectId}?t=${Date.now()}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        const data = await response.json();
                        
                        if (!data.hasActiveProcessing) {
                            console.log('[Upload] Session found in localStorage but backend says processing finished. Clearing session.');
                            localStorage.removeItem(key);
                            setStage('drop');
                            setFilesStats([]);
                            setOperationId(null);
                            setInitialProcessingState(null);
                            toast.info("O processamento anterior já foi concluído.");
                        } else {
                             // Salvar estado de processamento para restauração
                             if (data.state) {
                                 console.log('[Upload] Processing state restored:', data.state);
                                 setInitialProcessingState(data.state);
                             }
                             
                             // Não sobrescrever spedType - manter o valor detectado localmente
                             // O frontend já detecta corretamente se é mixed, icms ou pis
                        }
                    } catch (e) {
                        console.error("Error checking liveness:", e);
                    } finally {
                        setCheckingStatus(false);
                    }
                 };
                 checkAlive();

             } else {
                 localStorage.removeItem(key);
             }
         } catch(e) {
             console.error("[Upload] Error restoring session:", e);
             localStorage.removeItem(key);
         }
     }

     if (!restored) {
        const checkBackendStatus = async () => {
            try {
                const token = authService.getToken();
                        const response = await fetch(`${API_URL}/speds/processing-status/${projectId}?t=${Date.now()}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                const data = await response.json();
                
                if (data.hasActiveProcessing) {
                     setStage('processing');
                     let detectedSpedType = data.spedType;
                     
                     if (detectedSpedType === 'mixed') {
                         setFilesStats([{ tipoSped: 'ICMS_IPI' }, { tipoSped: 'PIS_COFINS' }]);
                     } else if (detectedSpedType === 'pis') {
                         setFilesStats([{ tipoSped: 'PIS_COFINS' }]); 
                     } else if (detectedSpedType === 'icms') {
                         setFilesStats([{ tipoSped: 'ICMS_IPI' }]);
                     }
                     
                     localStorage.setItem(key, JSON.stringify({
                        stage: 'processing',
                        operationId: null,
                        spedType: detectedSpedType,
                        timestamp: Date.now()
                     }));
                }
            } catch (e) {
                console.error('[Upload] Error checking backend status:', e);
            } finally {
                setCheckingStatus(false);
            }
        };
        checkBackendStatus();
     } else {
         setCheckingStatus(false);
     }
  }, [currentProject]);

  // Extensões conhecidas
  const SPED_EXTENSIONS = ['.txt', '.sped'];

  // Upload de arquivos (envia 1 por vez para não exceder limite Cloudflare de 100MB)
  const handleCompressedUpload = async (files, projectId) => {
    setUploadingZip(true);
    setStage('uploading');
    setProgress(0);

    const totalSize = files.reduce((acc, f) => acc + f.size, 0);
    const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
    setProgressText(`Preparando ${files.length} arquivo(s) (${totalSizeMB}MB)...`);

    try {
      const token = authService.getToken();
      const opId = generateOperationId();

      let uploadedBytes = 0;

      // Enviar cada arquivo individualmente
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
        setProgressText(`Enviando ${i + 1}/${files.length}: ${file.name} (${fileSizeMB}MB)`);

        await uploadSingleFile(
          `${API_URL}/projects/${projectId}/upload-single-file`,
          'file',
          file,
          token,
          { operationId: opId }
        );

        uploadedBytes += file.size;
        const percent = Math.round((uploadedBytes / totalSize) * 100);
        setProgress(percent);
        console.log(`[SmartUpload] File ${i + 1}/${files.length} uploaded: ${file.name}`);
      }

      setProgress(100);
      setProgressText('Todos os arquivos enviados! Iniciando processamento...');

      // Disparar processamento
      const startRes = await fetch(`${API_URL}/projects/${projectId}/start-upload-processing/${opId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao iniciar processamento');
      }

      // Transição para modal de progresso smart_upload
      setSmartUploadOperationId(opId);
      setSmartUploadActive(true);
      setStage('drop');
      setUploadingZip(false);
      setProgress(0);

    } catch (error) {
      console.error("Erro upload:", error);
      toast.error(`Erro no upload: ${error.message}`);
      setStage('drop');
      setProgress(0);
      setUploadingZip(false);
    } 
  };
  
  // (Rest of the file...)

  // 1. Seleção e Análise Local (identifica tipo automaticamente)
  const handleFilesSelect = useCallback(async (files) => {
    const projectId = currentProject?._id || authService.getSelectedProject();

    if (!projectId) {
      toast.error("Nenhum projeto selecionado. Selecione um projeto na barra de status.");
      return;
    }

    if (files.length === 0) {
      toast.warning("Nenhum arquivo selecionado");
      return;
    }

    // Separar por tipo
    const spedFiles = files.filter(f => SPED_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext)));
    const otherFiles = files.filter(f => !SPED_EXTENSIONS.some(ext => f.name.toLowerCase().endsWith(ext)));

    // Se há apenas SPEDs (.txt/.sped), vai pelo fluxo de review
    if (spedFiles.length > 0 && otherFiles.length === 0) {
      setStage('analyzing');
      setProgressText(`Analisando ${spedFiles.length} arquivo(s) SPED...`);

      try {
        const statsPromises = spedFiles.map(file => parseSpedFile(file));
        const statsResults = await Promise.all(statsPromises);
        
        setFilesStats(statsResults);
        setRawFiles(spedFiles);
        setStage('review');

      } catch (error) {
        console.error("Erro na análise:", error);
        toast.error("Erro ao ler arquivos. Verifique se são textos válidos.");
        setStage('drop');
      }
      return;
    }

    // Qualquer outro tipo de arquivo → Smart Upload (aceita tudo)
    await handleCompressedUpload(files, projectId);
  }, [currentProject, onVoltar]);

  // 2. Confirmação e Envio (arquivo por arquivo para evitar limite Cloudflare)
  const handleConfirmUpload = async (approvedFileNames = null) => {
    const projectId = currentProject?._id || authService.getSelectedProject();
    
    if (!projectId) {
      toast.error("Erro: ID do projeto não encontrado.");
      return;
    }

    // Filtrar arquivos se uma lista de aprovados for fornecida
    const filesToUpload = approvedFileNames 
      ? rawFiles.filter(f => approvedFileNames.includes(f.name))
      : rawFiles;

    if (filesToUpload.length === 0) {
      toast.error("Nenhum arquivo válido para processar.");
      return;
    }

    setStage('uploading');
    setProgress(0);
    
    const totalSize = filesToUpload.reduce((acc, f) => acc + f.size, 0);
    const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2);
    
    setProgressText(`Iniciando envio de ${filesToUpload.length} arquivo(s) SPED...`);

    try {
      const token = authService.getToken();

      let uploadedBytes = 0;

      // Enviar cada arquivo SPED individualmente para staging
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
        setProgressText(`Enviando ${i + 1}/${filesToUpload.length}: ${file.name} (${fileSizeMB}MB)`);

        await uploadSingleFile(
          `${API_URL}/speds/upload-single/${projectId}`,
          'sped',
          file,
          token
        );

        uploadedBytes += file.size;
        const percent = Math.round((uploadedBytes / totalSize) * 100);
        setProgress(percent);
        console.log(`[SPED Upload] File ${i + 1}/${filesToUpload.length} uploaded: ${file.name}`);
      }

      setProgress(100);
      setProgressText('Todos os arquivos enviados! Iniciando processamento...');

      // Disparar processamento de todos os arquivos em staging
      const startRes = await fetch(`${API_URL}/speds/start-processing/${projectId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao iniciar processamento');
      }

      const result = await startRes.json();
      console.log("Processing started:", result);
      
      // Determine SPED type for persistence
      const hasPis = filesStats.some(f => f.tipoSped === 'PIS_COFINS');
      const hasIcms = filesStats.some(f => f.tipoSped === 'ICMS_IPI');
      
      let spedType = result.spedType || 'icms';
      if (hasPis && hasIcms) {
        spedType = 'mixed';
      } else if (hasPis) {
        spedType = 'pis';
      } else if (hasIcms) {
        spedType = 'icms';
      }
      
      console.log("[Upload] Detected spedType for persistence:", spedType, { hasPis, hasIcms, filesStats: filesStats.map(f => f.tipoSped) });

      // Save state for persistence
      if (projectId) {
          localStorage.setItem(`ravena_active_upload_${projectId}`, JSON.stringify({
              stage: 'processing',
              operationId: null,
              spedType: spedType,
              timestamp: Date.now()
          }));
      }
      
      // Ir para tela de processamento
      setStage('processing');
      toast.success(`${filesToUpload.length} arquivos enviados! Acompanhe o processamento.`);

    } catch (error) {
      console.error("Upload error:", error);
      toast.error(`Erro no upload: ${error.message}`);
      setStage('review'); // Volta para review em caso de erro
    }
  };

  const handleCancelReview = () => {
    setStage('drop');
    setFilesStats([]);
    setRawFiles([]);
  };

  // Handlers de Drag & Drop
  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(event.dataTransfer.files);
    await handleFilesSelect(droppedFiles);
  };

  const handleFileInput = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await handleFilesSelect(files);
      event.target.value = "";
    }
  };

  // Calcular tipo de SPED para ProcessingScreen
  const getSpedType = () => {
    if (operationId) return 'xml';
    const hasIcms = filesStats.some(f => f.tipoSped === 'ICMS_IPI');
    const hasPis = filesStats.some(f => f.tipoSped === 'PIS_COFINS');
    if (hasIcms && hasPis) return 'mixed';
    if (hasPis) return 'pis';
    return 'icms';
  };

  const processingProjectId = currentProject?._id || authService.getSelectedProject();

  return (
    <>
    {/* ===== MODAL: Review Screen ===== */}
    {stage === 'review' && (
      <ReviewScreen 
        filesStats={filesStats}
        onConfirm={handleConfirmUpload}
        onCancel={handleCancelReview}
        currentProject={currentProject}
      />
    )}

    {/* ===== MODAL: Processing Screen (SPEDs) ===== */}
    {stage === 'processing' && (
      <ProcessingScreen 
        projectId={processingProjectId}
        spedType={getSpedType()}
        initialState={initialProcessingState}
        onComplete={() => {
          if (processingProjectId) localStorage.removeItem(`ravena_active_upload_${processingProjectId}`);
          setStage('drop');
          setFilesStats([]);
          setRawFiles([]);
          setOperationId(null);
          setInitialProcessingState(null);
          onVoltar && onVoltar();
        }}
        onError={(error) => {
          toast.error(`Erro no processamento: ${error}`);
        }}
      />
    )}

    {/* ===== MODAL: Smart Upload Progress ===== */}
    {smartUploadActive && (
      <UploadProgressModal
        projectId={processingProjectId}
        operationId={smartUploadOperationId}
        uploadProgress={100}
        onComplete={() => {
          setSmartUploadActive(false);
          setSmartUploadOperationId(null);
          toast.success("Upload processado com sucesso!");
          onVoltar && onVoltar();
        }}
        onCancel={() => {
          setSmartUploadActive(false);
          setSmartUploadOperationId(null);
        }}
      />
    )}
    
    {/* ===== Drop Area (always visible) ===== */}
    {checkingStatus ? (
      <Container>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: '#8b8d98'
        }}>
            <p>Verificando status do processamento...</p>
        </div>
      </Container>
    ) : (
    <Container
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={(e) => { 
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsDragging(false);
        }
      }}
      onDrop={handleDrop}
      className={isDragging ? 'dragging' : ''}
    >
      {onVoltar && !hideBackButton && (
        <button className="back-button" onClick={onVoltar}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            Ver Arquivos <GoArrowRight size={16} />
          </span>
        </button>
      )}

      <div className="upload-content">
        {/* Left side — Drop Area */}
        <div className="drop-area-section">
          <DropArea
            onClick={() => stage !== 'uploading' && fileInputRef.current?.click()}
            $isDragging={isDragging}
            $hasWarning={warning}
            $isProcessing={stage === 'analyzing' || stage === 'uploading'}
            $isSuccess={stage === 'success'}
          >
            <div className="drop-box">
              <div className="drop-area">
                <div className="drop-icon-ring">
                  <div className="drop-icon-inner">
                    {stage === 'success' ? <GoCheck /> : <IoCloudUploadOutline />}
                  </div>
                  {stage === 'drop' && !isDragging && (
                    <div className="ring-pulse" />
                  )}
                </div>
                
                <p className="title">
                  {stage === 'analyzing' ? "Analisando arquivos..." :
                   stage === 'uploading' ? "Enviando arquivos..." :
                   stage === 'success' ? "Upload concluído!" :
                   isDragging ? "Solte os arquivos aqui" :
                   "Solte arquivos aqui"}
                </p>

                <p className="subtitle">
                  {stage === 'analyzing' || stage === 'uploading' ? progressText :
                   stage === 'success' ? "Arquivos enviados para processamento" :
                   "ou clique para selecionar do computador"}
                </p>

                {stage === 'drop' && !isDragging && (
                  <div className="file-types">
                    <span className="file-badge">SPED</span>
                    <span className="file-badge">XML</span>
                    <span className="file-badge">ZIP</span>
                    <span className="file-badge">RAR</span>
                    <span className="file-badge">7z</span>
                    <span className="file-badge">Qualquer</span>
                  </div>
                )}

                {(stage === 'analyzing' || stage === 'uploading') && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: stage === 'uploading' ? `${progress}%` : '100%', animation: stage === 'analyzing' ? 'pulse 1.5s infinite' : 'none' }} />
                    </div>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="*"
                  onChange={handleFileInput}
                  style={{ display: "none" }}
                  disabled={stage !== 'drop'}
                />
              </div>
            </div>
          </DropArea>
        </div>

        {/* Right side — Branding & Features */}
        <div className="message-section">
          <div className="message">
            <div className="logo">
              <img src={VidalLogo} alt="Vidal" />
            </div>
            <h2>Envie seus arquivos</h2>
            <p>
              Arraste SPEDs, XMLs ou arquivos compactados. O Vidal analisa, processa e organiza tudo automaticamente.
            </p>
            <div className="features-list">
              {[
                { icon: HiOutlineDocumentText, text: 'SPED ICMS/IPI e PIS/COFINS' },
                { icon: HiOutlineCloudArrowUp, text: 'ZIP, RAR e 7z com extração automática' },
                { icon: HiOutlineCpuChip, text: 'Processamento inteligente' },
                { icon: HiOutlineShieldCheck, text: 'Validação fiscal integrada' },
              ].map((feat, i) => (
                <div key={i} className="feature-item" style={{ animationDelay: `${i * 100 + 200}ms` }}>
                  <div className="feature-icon">
                    <feat.icon size={16} />
                  </div>
                  <span>{feat.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Container>
    )}
    </>
  );
}
